import { useLocation, useNavigate } from "react-router-dom";
import { useDeleteEcoActivityComment, useGetEcoActivityDetails, useGetEcoActivityUserComments, useUpdateEcoActivity } from "../../../queries/facts";
import { useForm } from "react-hook-form";
import MediaUpload from "../../../components/Forms/MediaUpload";
import { useEffect, useMemo, useState } from "react";
import { ActionButton } from "../../../components/Forms/ActionButton";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { createColumnHelper } from "@tanstack/react-table";
import TanStackTable from "../../../components/TanStackTable";
import { FaTrash } from "react-icons/fa";
import { formatDateTime } from "../../../utils/formatters/dateTimeFormat";
import ActiveStatus from "../../../components/Common/ActiveStatus";

const EditEcoActivityDetails = () => {

    const navigate = useNavigate();
    const { state } = useLocation();
    const id = state?.id;

    const columnHelper = createColumnHelper();

    const { data, isPending, refetch } = useGetEcoActivityDetails(id);
    const { data: likes, isPaused: isLikesPending } = useGetEcoActivityUserComments({ id: data?._id, refType: "EcoActivity" });

    const [coverImage, setCoverImage] = useState();
    const [showInHomePage, setShowInHomePage] = useState(data?.showInHomePage);
    const [mediaClear, setMediaClear] = useState(false);
    // initialize to false (so UI doesn't show 'Inactive' before data is loaded)
    const [active, setActive] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        reset,
    } = useForm({
        defaultValues: {
            userName: data ? data?.userName : "",
            createdAt: data && data?.createdAt ? new Date(data?.createdAt).toISOString().split('T')[0] : "",
            title: data ? data?.title : "",
            hashTag: data ? data?.hashTag : "",
            showInHomePage: data ? data?.showInHomePage : false,
        }
    });

    const { mutateAsync, isPending: isUpdating } = useUpdateEcoActivity();
    const { mutateAsync: deleteComment, isPending: isDeletingComment } = useDeleteEcoActivityComment();

    const deleteCommentHandler = async (commentId) => {
        const confirm = window.confirm("Are you sure you want to delete this comment?");
        if (!confirm) return;
        await deleteComment({ commentId: commentId, refId: data?._id, refType: "EcoActivity" }, {
            onSuccess: () => {
                toast.success("Comment deleted successfully");
                refetch();
            },
            onError: (error) => {
                toast.error("Error deleting comment");
                console.error("Error deleting comment:", error);
            }
        });
    }

    const onSubmit = async (formData) => {
        console.log({
            title: formData.title,
            hashTag: formData.hashTag,
            isBoth: false,
            userId: data.userId,
            showInHomePage: showInHomePage,
            id: id,
        });
        if (!coverImage) {
            alert("Please upload an activity image.");
            return;
        }
        await mutateAsync({
            active: active,
            title: formData.title,
            hashTag: formData.hashTag,
            isBoth: false,
            userId: data.userId,
            userName: formData.userName,
            showInHomePage: showInHomePage,
            id: id,
            file: coverImage,
        }, {
            onSuccess: () => {
                toast.success("Eco Activity updated successfully");
                setTimeout(() => {
                    navigate("/eco-activity/list-eco-activity");
                }, 2000);
            },
            onError: (error) => {
                toast.error("Error updating Eco Activity");
                console.error("Error updating Eco Activity:", error);
            }
        });
    }

    const columns = useMemo(() => [
        columnHelper.display({
            id: 'srno',
            header: 'Sr No.',
            cell: info => info.row.index + 1,
            size: 80, // Fixed narrow width
        }),
        columnHelper.accessor('username', {
            header: 'User Name',
            cell: info => (
                <div className="line-clamp-1 flex items-center gap-2">
                    <img src={info.row.original?.createdBy?.profilepic} alt="-" className="size-5 rounded-full" />
                    {info.row.original?.createdBy?.username || 'N/A'}
                </div>
            ),
            size: 150, // Fixed width
        }),
        columnHelper.accessor('description', {
            header: 'Comment',
            cell: info => (
                <div className="line-clamp-1 w-full">
                    {info.getValue() || '-'}
                </div>
            ),
            size: 'auto', // Will take up remaining space
            minSize: 200, // Minimum width
        }),
        {
            accessorKey: 'createdAt',
            header: 'Date & Time',
            meta: {
                filterVariant: 'date',
            },
            cell: ({ getValue }) => {
                const value = getValue();
                return value ? formatDateTime(value) : '-';
            },
            size: 120, // Fixed width
        },
        columnHelper.accessor('actions', {
            header: 'Actions',
            enableColumnFilter: false,
            cell: info => {
                return (
                    <div className='flex items-center justify-center'>
                        <button
                            className="rounded p-1.5 bg-[rgba(199,52,52,0.1)] hover:bg-[rgba(199,52,52,0.2)] mx-1 min-h-7"
                            onClick={() => {
                                deleteCommentHandler(info.row.original._id);
                            }}
                        >
                            <FaTrash className="fill-chipRed" />
                        </button>
                    </div>
                )
            },
            size: 80, // Fixed width
        }),
    ], [navigate]);

    useEffect(() => {
        setMediaClear(false);
    }, [coverImage]);

    useEffect(() => {
        setTimeout(() => {
            if (data) {
                setCoverImage(data.imageUrl);
                setValue("description", data.description || "");
            }
        }, 500);
    }, [data]);

    useEffect(() => {
        if (data) {
            setValue('createdAt', data.createdAt ? new Date(data.createdAt).toISOString().split('T')[0] : "");
            setValue('userName', data.userName);
            setValue('title', data.title);
            setValue('hashTag', data.hashTag);
            setValue('showInHomePage', data.showInHomePage);
            setCoverImage(data.imageUrl || "");
            setShowInHomePage(data.showInHomePage);
            // Ensure status toggle reflects actual value after data arrives
            setActive(Boolean(data.active));
        }
    }, [data, setValue]);

    return (
        <div className="">
            <div className="flex items-center justify-end mb-2">
                <ActiveStatus
                    active={active}
                    setActive={setActive}
                />
            </div>
            <form className="flex flex-col p-5 w-full bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full" onSubmit={handleSubmit(onSubmit)}>
                <div className="flex flex-col w-full max-md:max-w-full">
                    <h1 className="self-start text-2xl text-primary font-bold">Edit Eco Activity</h1>
                    <div className="flex gap-3 justify-between">
                        <div className="flex flex-col gap-3 w-full">
                            {/* User Name */}
                            <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
                                <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">User Name</label>
                                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                    <input
                                        type="text"
                                        placeholder="User Name"
                                        {...register("userName", { required: "User name is required" })}
                                        className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.userName ? "border-red-500" : ""}`}
                                    />
                                </div>
                                {errors.userName && <p className="text-red text-sm">{errors.userName.message}</p>}
                            </div>
                            {/* Date */}
                            <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
                                <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Date</label>
                                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                    <input
                                        type="date"
                                        placeholder="Date"
                                        {...register("createdAt", { required: "Date is required" })}
                                        className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.createdAt ? "border-red-500" : ""}`}
                                    />
                                </div>
                                {errors.createdAt && <p className="text-red text-sm">{errors.createdAt.message}</p>}
                            </div>
                            {/* Title */}
                            <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
                                <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Title</label>
                                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                    <input
                                        type="text"
                                        placeholder="Title"
                                        {...register("title", { required: "Title is required" })}
                                        className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.title ? "border-red-500" : ""}`}
                                    />
                                </div>
                                {errors.title && <p className="text-red text-sm">{errors.title.message}</p>}
                            </div>
                            {/* Show in Home Page */}
                            <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
                                <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Select The Page To Show Eco Activity</label>
                                <div className="flex items-center mt-1 min-h-10 px-3 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400 gap-5">
                                    <div>
                                        <input type="checkbox" name="showInHomePage" id="" value={showInHomePage} onChange={() => setShowInHomePage(!showInHomePage)} checked={showInHomePage} />
                                        <span className="ml-2">Home Page</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* Activity Media */}
                        <div className="mt-0">
                            <MediaUpload
                                clearMedia={mediaClear}
                                label={"Thumbnail"}
                                existingImage={coverImage}
                                setImageUpload={setCoverImage}
                                stateImage={coverImage}
                                setStateImageUpload={setCoverImage}
                            />
                        </div>
                    </div>
                    {/* Hash Tags */}
                    <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-3">
                        <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Hash Tags</label>
                        <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                            <input
                                type="text"
                                placeholder="Hash Tags (#vegan #plantbased)"
                                {...register("hashTag", { required: "Hash Tags required" })}
                                className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.title ? "border-red-500" : ""}`}
                            />
                        </div>
                        {errors.title && <p className="text-red text-sm">{errors.title.message}</p>}
                    </div>
                </div>

                {/* Buttons */}
                <div className="mt-10 flex gap-5 items-center justify-between">
                    <ActionButton
                        variant="reset"
                        text="Reset"
                        handleSubmit={() => {
                            // reset({
                            //     userName: "",
                            //     createdAt: "",
                            //     title: "",
                            //     hashTag: "",
                            //     showInHomePage: false,
                            //     description: ""
                            // });
                            setCoverImage(null);
                            setMediaClear(true);
                            refetch();
                            if (data) {
                                setValue('createdAt', data.createdAt ? new Date(data.createdAt).toISOString().split('T')[0] : "");
                                setValue('userName', data.userName);
                                setValue('title', data.title);
                                setValue('hashTag', data.hashTag);
                                setValue('showInHomePage', data.showInHomePage);
                                setShowInHomePage(data.showInHomePage);
                            }
                            setTimeout(() => {
                                setCoverImage(data.imageUrl);
                            }, 500);
                        }}
                    />
                    <div className="flex items-center gap-3">
                        <ActionButton
                            variant="cancel"
                            text="Cancel"
                            handleSubmit={() => navigate("/eco-activity/list-eco-activity")}
                        />
                        <ActionButton
                            variant="submit"
                            text="Save"
                            // onClick={() => reset()}
                            disabled={isUpdating
                                // || isEditingBlog
                            }
                        />
                    </div>
                </div>
            </form>
            <div className="flex flex-col p-5 w-full bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] mt-5">
                <h2 className="font-bold text-base mb-4">Eco Activity User Comments</h2>
                <TanStackTable
                    data={likes}
                    columns={columns}
                    enablePagination={true}
                />
            </div>
            <ToastContainer
                position="top-right"
            />
        </div>
    )
}

export default EditEcoActivityDetails;